import { gql } from "@apollo/client";

export const GET_OWNER_ROLE_ID = gql`
  query getOwnerRoleId {
    job_roles(where: { access_type: { _eq: owner } }) {
      role_id
    }
  }
`;

export const GET_USER_DETAILS = gql`
  query getUsersById($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      employee_code
      profile_image
      user_id
      email
      phone
      first_name
      is_email_verified
      is_phone_verified
      last_name
      last_screen
      verification_code
      time_zone
      user_location_maps(distinct_on: job_role_id) {
        business_id
        jobRoleDetails {
          accessDetails {
            type_id
            type_name
          }
          role_name
          role_id
        }
        location_id
        locationDetails {
          location_id
          location_name
          location_timings {
            closing_time
            opening_time
            working_day
            location_id
          }
        }
      }
      user_shifts {
        working_day
        start_time
        end_time
      }
      businesses {
        location_count
        location_user_count_maps_aggregate {
          aggregate {
            count
          }
        }
      }
    }
  }
`;

export const GET_CUSTOMER_TRANSACTION = gql`
  query getCustomerTransaction($user_id: uuid!) {
    user_subscription(where: { user_id: { _eq: $user_id } }) {
      stripe_subscription_id
      user_subscription_id
    }
  }
`;

export const GET_BUSINESS_BY_OWNER = gql`
  query getBusinessByOwner($owner_id: uuid!) {
    business: getBusinesses(where: { owner_id: { _eq: $owner_id } }) {
      business_name
      business_type_id
      business_id
      owner_id
      location_count
      brand_logo
      brand_icon
      ref_code
      owner {
        is_email_verified
        is_phone_verified
        first_name
        last_screen
        email
      }
      business_type {
        business_type_description
        business_type_name
      }
    }
  }
`;

export const GET_BUSINESS_TYPES = gql`
  query business_type {
    business_types {
      id: business_type_id
      name: business_type_name
      description: business_type_description
    }
  }
`;

export const GET_PRICE_PACKAGES = gql`
  query getPricePackages {
    get_stripe_setup {
      prices
      publishableKey
    }
  }
`;

export const GET_PRICE_PACKAGES_LIST = gql`
  query getPricePackagesList {
    packages(
      order_by: { display_order: asc }
      where: { is_active: { _eq: true } }
    ) {
      id
      name
      description
      allow_custom_task
      allow_qr_code
      stripe_product_id
      package_prices(
        order_by: { display_order: asc }
        where: { is_active: { _eq: true } }
      ) {
        id
        price
        price_description
        stripe_price_id
        billing_period
        free_trial_days
      }
    }
  }
`;

export const CREATE_CHECKOUT_SESSION = gql`
  query createCheckoutSession($locations: Float!, $price_id: String!) {
    create_checkout_session(locations: $locations, price_id: $price_id) {
      session
      session_id
    }
  }
`;

export const CREATE_PAYMENT_INTENT = gql`
  query createPaymentIntent($locations: Float!, $price_id: String!) {
    create_payment_intent(locations: $locations, price_id: $price_id) {
      clientSecret
    }
  }
`;

export const GET_LAST_SCREEN = gql`
  query getLastScreen($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      last_screen
    }
  }
`;

export const GET_LOCATION_DETAILS_BY_OWNER = gql`
  query getLocationDetailsByBusinessOwner($user_id: uuid!) {
    userDetails: getUsersById(user_id: $user_id) {
      last_screen
    }
    locationsByOwner: user_location_map_aggregate(
      distinct_on: location_id
      where: { businessDetails: { owner_id: { _eq: $user_id } } }
    ) {
      locations: nodes {
        locationDetails {
          location_name
          location_id
          city
          total_employees
          timezone_name
          timezone_id
          country
          state
          street_address
          zipcode
          workingHours: location_timings {
            working_day
            opening_time
            closing_time
          }
        }
        ownerId: user_id
      }
    }
    businesses: getBusinesses(where: { owner_id: { _eq: $user_id } }) {
      business_id
      location_count
      business_type_id
      owner_id
      business_name
      ref_code
    }
    roleDetails: job_roles(where: { access_type: { _eq: owner } }) {
      role_id
      role_name
    }
  }
`;

export const GET_EMPLOYEE_ROLES_AND_LOCATIONS_FOR_BUSINESS = gql`
  query getLocationsAndUserRolesForBusiness(
    $user_id: uuid!
    $business_id: uuid!
    $_nin: [access_types_enum!] = [area_manager, owner]
  ) {
    job_roles(
      where: {
        access_type: { _nin: $_nin }
        business_type_job_role_maps: {
          business_type: { businesses: { business_id: { _eq: $business_id } } }
        }
      }
    ) {
      role_id
      role_name
    }
    getBusinesses(where: { owner_id: { _eq: $user_id } }) {
      business_name
      business_id
      user_location_maps(where: { user_id: { _eq: $user_id } }) {
        locationDetails {
          location_id
          location_name
          total_employees
          user_location_maps {
            user_id
            job_role_id
            jobRoleDetails {
              role_name
            }
            userDetails {
              first_name
              email
            }
          }
          savedCounts: location_user_count_maps_aggregate {
            aggregate {
              sum {
                count
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_LOCATION_EMPLOYEE_DETAILS = gql`
  query getLocationEmployeeDetails($owner_id: uuid!) {
    businesses: getBusinesses(where: { owner_id: { _eq: $owner_id } }) {
      business_id
    }
    allLocations: getLocations(
      where: {
        user_location_maps: {
          businessDetails: { owner_id: { _eq: $owner_id } }
        }
      }
    ) {
      location_id
      location_name
      workingHours: location_timings {
        opening_time
        closing_time
        working_day
      }
      userTypes: location_user_count_maps {
        roleDetails: job_role {
          roleId: role_id
          roleName: role_name
        }
        count
      }
      existingUserData: user_location_maps(
        where: {
          jobRoleDetails: { access_type: { _nin: [area_manager, owner] } }
        }
        order_by: { userDetails: { employee_code: asc } }
      ) {
        jobRoleDetails {
          role_id
          role_name
          access_type
        }
        userDetails {
          first_name
          last_name
          user_id
          phone
          email
          employee_code
          user_shifts {
            working_day
            start_time
            end_time
          }
        }
      }
    }
  }
`;

export const GET_SHIFT_TIMINGS = gql`
  query getUserShiftTimings($user_id: uuid!) {
    user_shift(where: { user_id: { _eq: $user_id } }) {
      working_day
      start_time
      end_time
    }
    userRoles: getUserLocationMaps(where: { user_id: { _eq: $user_id } }) {
      jobRoleDetails {
        access_type
      }
    }
  }
`;

export const GET_JOB_ROLES_BY_ID = gql`
  query job_roles($user_id: uuid) {
    job_roles(where: { users: { user_id: { _eq: $user_id } } }) {
      access_type
      role_id
      role_name
      users {
        userDetails {
          last_screen
        }
      }
      accessDetails {
        type_id
        type_name
      }
    }
  }
`;

export const GET_LOCATIONS_AND_AREA_MANAGER_ID = gql`
  query getLocationsAndAreaManagerRole($owner_id: uuid!) {
    locations: getLocations(
      where: {
        user_location_maps: {
          businessDetails: { owner_id: { _eq: $owner_id } }
        }
      }
    ) {
      location_id
      location_name
      location_timings {
        working_day
        opening_time
        closing_time
      }
    }
    roles: job_roles(where: { access_type: { _eq: area_manager } }) {
      role_id
      role_name
    }
    businesses: getBusinesses(where: { owner_id: { _eq: $owner_id } }) {
      business_id
      business_name
    }
    areaManagers: getUsers(
      where: {
        user_location_maps: {
          businessDetails: { owner_id: { _eq: $owner_id } }
          jobRoleDetails: { access_type: { _eq: area_manager } }
        }
      }
      order_by: { employee_code: asc }
    ) {
      user_id
      first_name
      last_name
      email
      phone
      employee_code
      locations: user_location_maps {
        locationDetails {
          location_id
          location_name
        }
      }
    }
  }
`;

export const GET_USERS_COUNT_FOR_LOCATION = gql`
  query getUsersCountForLocation($owner_id: uuid!) {
    users_for_location: location_user_count_map(
      where: { business: { owner_id: { _eq: $owner_id } } }
    ) {
      job_role {
        role_id
        role_name
      }
      count
      location_id
    }
  }
`;

export const GET_EMPLOYEE_REPORTING_LIST_FOR_TABLE = gql`
  query getEmployeeReportingListForTable($locationIds: [uuid!]!) {
    usersData: user_reporting_map(
      where: { location_id: { _in: $locationIds } }
    ) {
      reportee_details {
        user_id
        first_name
        last_name
        employee_code
      }
      reporter_details {
        user_id
        first_name
        last_name
      }
      location_details {
        location_id
        location_name
      }
    }
  }
`;

export const GET_OWNER_LOCATIONS = gql`
  query location($user_id: uuid) {
    location: getUserLocationMaps(where: { user_id: { _eq: $user_id } }) {
      location_id
      locationDetails {
        location_name
      }
    }
  }
`;

export const GET_EMPLOYEE_LIST = gql`
  query getEmployeesByLocation(
    $user_id: uuid!
    $email: String
    $employee_code: String
    $name: String
    $role: String
    $location: String
  ) {
    employeesByLocation: getUserLocationMaps(
      where: { user_id: { _eq: $user_id } }
    ) {
      locationDetails {
        location_id
        location_name
        users: user_location_maps(
          where: {
            userDetails: {
              email: { _ilike: $email }
              _and: {
                employee_code: { _ilike: $employee_code }
                _and: {
                  _or: {
                    last_name: { _ilike: $name }
                    first_name: { _ilike: $name }
                  }
                }
              }
            }
            jobRoleDetails: { role_name: { _ilike: $role } }
            locationDetails: { location_name: { _ilike: $location } }
          }
        ) {
          userDetails {
            first_name
            last_name
            employee_code
            email
            profile_image
            user_id
          }
          userRole: jobRoleDetails {
            role_id
            role_name
            accessDetails {
              type_id
              type_name
            }
          }
        }
      }
      currentUserAccessDetails: jobRoleDetails {
        accessDetails {
          type_id
          type_name
        }
      }
    }
  }
`;

export const GET_EMPLOYEES_BY_LOCATION = gql`
  query getEmployeesByLocation(
    $location_id: uuid!
    $access_type_id_nums: [String!]
  ) {
    usersData: getLocationsById(location_id: $location_id) {
      location_id
      location_name
      empData: user_location_maps(
        where: {
          jobRoleDetails: {
            accessDetails: { type_id: { _in: $access_type_id_nums } }
          }
        }
      ) {
        userDetails {
          first_name
          last_name
          user_id
        }
        roleData: jobRoleDetails {
          role_id
          role_name
          accessDetails {
            type_id
          }
        }
      }
    }
  }
`;

export const GET_LOCATION_LIST = gql`
  query getUserLocationMaps(
    $user_id: uuid
    $type_id: String
    $working_day: String
    $state: String
    $city: String
    $country: String
    $zipcode: String
    $location_name: String
  ) {
    getUserLocationMaps(
      where: {
        user_id: { _eq: $user_id }
        locationDetails: {
          state: { _ilike: $state }
          city: { _ilike: $city }
          country: { _ilike: $country }
          zipcode: { _ilike: $zipcode }
          location_name: { _ilike: $location_name }
        }
      }
    ) {
      locationDetails {
        location_name
        state
        street_address
        city
        country
        location_id
        zipcode
        location_timings(where: { working_day: { _eq: $working_day } }) {
          working_day
          opening_time
          closing_time
        }
        user_location_maps(
          where: {
            jobRoleDetails: { accessDetails: { type_id: { _gt: $type_id } } }
          }
        ) {
          userDetails {
            first_name
            last_name
            user_id
          }
        }
      }
    }
  }
`;

export const GET_BUSINESS_DETAILS = gql`
  query getBusinessDetails($user_id: uuid) {
    businessDetailsByUser: getUserLocationMaps(
      where: { user_id: { _eq: $user_id } }
      distinct_on: business_id
    ) {
      userDetails: jobRoleDetails {
        accessDetails {
          access_level: type_id
          user_type: type_name
        }
      }
      businessDetails {
        business_id
        business_name
        locations: user_location_maps(
          distinct_on: location_id
          where: { user_id: { _eq: $user_id } }
        ) {
          locationDetails {
            location_id
            location_name
            workingHours: location_timings {
              opening_time
              closing_time
              working_day
            }
          }
        }
        rolesByBusiness: business_type {
          roleList: business_type_job_role_maps {
            job_role {
              role_id
              role_name
              accessDetails {
                access_level: type_id
                user_type: type_name
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_LOCATION_BY_ID = gql`
  query getUserLocationMaps(
    $user_id: uuid
    $type_id: String
    $working_day: String
    $location_id: uuid
    $name: String
    $role_name: String
    $fname: String
    $lname: String
  ) {
    getUserLocationMaps(
      where: { user_id: { _eq: $user_id }, location_id: { _eq: $location_id } }
    ) {
      locationDetails {
        location_name
        state
        street_address
        city
        country
        location_id
        zipcode
        timezone_name
        location_timings(where: { working_day: { _eq: $working_day } }) {
          working_day
          opening_time
          closing_time
        }
        user_location_maps(
          where: {
            jobRoleDetails: {
              accessDetails: { type_id: { _gt: $type_id } }
              role_name: { _ilike: $role_name }
            }
            userDetails: {
              _and: {
                _or: [
                  { first_name: { _ilike: $fname } }
                  { first_name: { _ilike: $lname } }
                  { last_name: { _ilike: $fname } }
                  { last_name: { _ilike: $lname } }
                ]
              }
            }
          }
        ) {
          userDetails {
            first_name
            last_name
            user_id
          }
          jobRoleDetails {
            role_name
            role_id
          }
        }
      }
    }
  }
`;

export const GET_REPORTING_MANAGERS_BY_LOCATION_AND_ACCESS_TYPE = gql`
  query getReportingManagersByLocationAndAccessType(
    $location_id: uuid!
    $employee_access_id: String!
  ) {
    users: getUserLocationMaps(
      where: {
        location_id: { _eq: $location_id }
        jobRoleDetails: {
          accessDetails: { type_id: { _lt: $employee_access_id } }
        }
      }
    ) {
      userDetails {
        first_name
        last_name
        user_id
      }
    }
  }
`;

export const GET_USER_ID_BY_EMAIL = gql`
  query getUserIdByEmail($email: String!) {
    users: getUsers(where: { email: { _eq: $email } }) {
      user_id
    }
  }
`;

export const GET_ANNOUNCEMENTS_LIST = gql`
  query getAnnouncements(
    $user_id: uuid!
    $message: String
    $announcement_startdate: timestamp
    $announcement_enddate: timestamp
  ) {
    announcements: announcement_user_map(
      where: {
        announcementDetails: { created_by: { _eq: $user_id } }
        _or: {
          announcementDetails: { announcement_message: { _ilike: $message } }
          _or: {
            announcementDetails: {
              created_at: { _gte: $announcement_startdate }
              _and: { created_at: { _lte: $announcement_enddate } }
            }
          }
        }
      }
      distinct_on: announcement_id
    ) {
      announcementDetails {
        announcement_message
        created_at
        users: announcement_user_maps {
          userDetails {
            first_name
            last_name
            user_id
            profile_image
          }
        }
      }
    }
  }
`;

export const GET_USER_VACATIONS = gql`
  query user_vacations($user_id: uuid) {
    user_vacations(where: { user_id: { _eq: $user_id } }) {
      end_date
      start_date
      user_id
      vacation_id
      vacation_reason
    }
  }
`;

export const GET_USER_VACATIONS_BY_BUSINESS = gql`
  query getEmployeesOnLeave(
    $user_id: uuid!
    $userAccessType: String
    $todaysDate: date
  ) {
    getUserLocationMaps(where: { user_id: { _eq: $user_id } }) {
      locationDetails {
        user_location_maps(
          where: {
            jobRoleDetails: {
              accessDetails: { type_id: { _gt: $userAccessType } }
              _and: {
                users: {
                  userDetails: {
                    user_vacations: {
                      end_date: { _gte: $todaysDate }
                      start_date: { _lte: $todaysDate }
                    }
                  }
                }
              }
            }
          }
        ) {
          userDetails {
            first_name
            last_name
            profile_image
            user_id
            user_vacations {
              start_date
              end_date
            }
            user_location_maps {
              jobRoleDetails {
                access_type
                role_name
              }
              locationDetails {
                city
                location_name
                state
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_USERS = gql`
  query getUserLocationMaps(
    $user_id: uuid!
    $userAccessType: String
    $_eq: uuid
    $_eq1: uuid
  ) {
    getUserLocationMaps(where: { user_id: { _eq: $user_id } }) {
      locationDetails {
        user_location_maps(
          where: {
            location_id: { _eq: $_eq }
            jobRoleDetails: {
              accessDetails: { type_id: { _gt: $userAccessType } }
            }
            job_role_id: { _eq: $_eq1 }
          }
        ) {
          userDetails {
            first_name
            last_name
            profile_image
            user_id
            user_tasks_maps {
              task_id
            }
          }
          jobRoleDetails {
            role_id
            role_name
          }
          locationDetails {
            location_name
          }
        }
        location_name
        location_id
      }
    }
  }
`;

export const GET_TASKS = gql`
  query tasks($role_id: uuid) {
    tasks(where: { task_role_id: { _eq: $role_id } }) {
      task_id
      description
    }
  }
`;

export const GET_TASK_TYPES = gql`
  query getTaskTypes {
    task_types {
      type_id: type
      type_name
    }
  }
`;

export const GET_TASK_RECURRING_TYPES = gql`
  query getTaskRecurringTypes {
    task_recurring_types {
      recurring_type
      recurring_type_name
    }
  }
`;

export const GET_TUTORIAL_TYPES = gql`
  query getTutorialTypes {
    tutorial_types {
      type_id
      type_name
    }
  }
`;

export const GET_CUSTOM_TASK = gql`
  query getTaskById($task_id: uuid!) {
    tasks_by_pk(task_id: $task_id) {
      task_id
      task_type
      description
      task_tutorial_maps {
        tutorial {
          tutorial_id
          tutorial_type
          tutorial_url
        }
      }
      user_tasks_maps {
        required_attachment
      }
      is_custom
      recurring_end_date
      recurring_start_date
      task_json
      task_recurring_type
    }
  }
`;
export const GET_LOCATION_AND_ROLES = gql`
  query MyQuery(
    $user_id: uuid
    $userAccessType: String
    $location: uuid
    $role: uuid
  ) {
    locationList: getUserLocationMaps(
      where: { user_id: { _eq: $user_id } }
      distinct_on: location_id
    ) {
      locationDetails {
        location_id
        location_name
      }
    }
    jobRoleList: getUserLocationMaps(where: { user_id: { _eq: $user_id } }) {
      locationDetails {
        user_location_maps(
          distinct_on: job_role_id
          where: {
            jobRoleDetails: {
              accessDetails: { type_id: { _gt: $userAccessType } }
            }
          }
        ) {
          jobRoleDetails {
            role_id
            role_name
          }
        }
      }
    }
  }
`;
export const GET_ALL_USERS = gql`
  query MyQuery(
    $user_id: uuid
    $userAccessType: String
    $location: uuid
    $role: uuid
  ) {
    userList: getUserLocationMaps(
      where: {
        user_id: { _eq: $user_id }
        _and: { location_id: { _eq: $location } }
      }
    ) {
      locationDetails {
        user_location_maps(
          where: {
            jobRoleDetails: {
              role_id: { _eq: $role }
              accessDetails: { type_id: { _gt: $userAccessType } }
            }
          }
        ) {
          userDetails {
            profile_image
            user_id
            first_name
            last_name
          }
          jobRoleDetails {
            role_id
            role_name
          }
          locationDetails {
            location_name
          }
        }
      }
    }
  }
`;
export const GET_USER_TIMEZONE = gql`
  query getUserTimezone($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      time_zone
    }
  }
`;

export const GET_USER_NAME = gql`
  query getUserTimezone($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      first_name
      last_name
    }
  }
`;

export const GET_CARD_DETAILS = gql`
  query getCardDetails {
    get_payment_methods {
      cards
    }
  }
`;

export const GET_TOP_3_PERFORMERS = gql`
  query getTop3Performers($user_id: uuid, $month: float8) {
    user_reporting_view(
      where: {
        leader_id: { _eq: $user_id }
        subordinate_details: {
          user_task_percentage_by_month: {
            month: { _eq: $month }
            completedperc: { _gt: "0" }
          }
        }
      }
      order_by: {
        subordinate_details: {
          user_task_percentage_by_month: { completedperc: desc_nulls_last }
        }
      }
      limit: 3
    ) {
      subordinate_details {
        user_task_percentage_by_month {
          completedperc
        }
        first_name
        last_name
        full_name
        profile_image
        user_id
      }
      location_details {
        location_name
      }
    }
  }
`;
export const GET_TIMEZONE = gql`
  query getLocations($user_id: uuid) {
    getLocations(
      where: { user_location_maps: { user_id: { _eq: $user_id } } }
      distinct_on: timezone_name
    ) {
      timezone_id
      timezone_name
    }
  }
`;

export const VALIDATE_REFCODE = gql`
  query validateReferralCode($ref_code: String) {
    agent(where: { ref_code: { _eq: $ref_code } }) {
      agent_id
      ref_code
    }
  }
`;

export const GET_LOCATION_TIMING = gql`
  query MyQuery($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      user_id
      user_location_maps {
        locationDetails {
          location_id
          location_name
          location_timings {
            closing_time
            opening_time
            working_day
            location_id
          }
        }
      }
    }
  }
`;
export const GET_USER_SHIFTS_DAY = gql`
  query getUsersById($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      user_id
      user_shifts {
        working_day
        start_time
        end_time
      }
    }
  }
`;

export const GET_SINGLE_TASK_DETAILS = gql`
  query getSingleTaskDetails($user_task_id: uuid!) {
    user_tasks_map(where: { user_task_id: { _eq: $user_task_id } }) {
      user_task_id
      task_id
      task_status
      task_date
      assigned_at
      task {
        task_type
        description
        taskTypeByTaskType {
          type
          type_name
        }
        user_tasks_maps(where: { user_task_id: { _eq: $user_task_id } }) {
          note
          task_status
          task_status_updated_at
        }
      }
      user {
        full_name
        user_id
        user_location_maps {
          locationDetails {
            city
            country
            location_id
            location_name
            state
            street_address
          }
          jobRoleDetails {
            role_id
            role_name
          }
        }
      }
      userByTaskStatusUpdatedBy {
        full_name
        user_location_maps {
          locationDetails {
            city
            country
            location_id
            location_name
            state
            street_address
          }
          jobRoleDetails {
            role_id
            role_name
          }
        }
        updated_at
      }
      task_status_updated_at
      task_files {
        file_name
        file_type
        id
      }
      required_attachment
    }
  }
`;

export const GET_EMAIL_PHONE_DETAILS = gql`
  query getEmailPhoneDetails($email: String = "", $phone: String = "") {
    getUsers(
      where: {
        _or: [{ email: { _ilike: $email } }, { phone: { _eq: $phone } }]
      }
    ) {
      email
      phone
    }
  }
`;
export const GET_USER_ACCESS = gql`
  query getUserAccess($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      user_location_maps {
        businessDetails {
          owner {
            userSubscription {
              package_price {
                package {
                  allow_custom_task
                  allow_qr_code
                }
              }
            }
          }
        }
      }
      email
      full_name
      user_id
    }
  }
`;
export const GET_UPCOMING_INVOICE = gql`
  query getUpcomingInvoice {
    get_upcoming_invoice {
      upcomingInvoice
    }
  }`;
export const GET_CURRENT_SUBSCRIPTIONS = gql`
query getSubscriptions {
  get_subscriptions {
    subscriptions
  }
}`;
